import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  FileText, Send, Check, Clock, User, Loader2, ArrowRight, Eye, X,
  ChevronDown, ChevronRight, Stethoscope, Printer, Mail, Sparkles, FlaskConical, ScanLine,
  ExternalLink
} from 'lucide-react';

function _formatTime(isoString) {
  if (!isoString) return '';
  try {
    const d = new Date(isoString);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return ''; }
}

function MyDocumentsBucketView() {
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [viewingPdf, setViewingPdf] = useState(null);

  const fetchDocs = useCallback(() => {
    api.get('/documents/my-queue').then(r => setDocs(r.data.documents || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const needsAttention = docs.filter(d => d.review_status !== 'signed' && d.triage_status !== 'filed');
  const workflowPending = docs.filter(d => d.review_status === 'signed' && d.triage_status === 'routed');

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>;

  if (docs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="p-4 rounded-2xl bg-muted/50 mb-4"><FileText className="w-8 h-8 text-muted-foreground" /></div>
        <p className="text-sm font-medium mb-1">No documents to review</p>
        <p className="text-xs text-muted-foreground">Documents routed to you will appear here</p>
      </div>
    );
  }

  const CATEGORY_ICONS = {
    lab_result: FlaskConical, imaging_report: ScanLine, consult_note: Stethoscope,
  };

  // Document sign-off overlay
  if (selectedDoc) {
    return (
      <DocSignOffOverlay doc={selectedDoc} onClose={() => { setSelectedDoc(null); fetchDocs(); }}
        viewingPdf={viewingPdf} setViewingPdf={setViewingPdf} />
    );
  }

  const renderCard = (doc, borderColor) => {
    const CatIcon = CATEGORY_ICONS[doc.suggested_category] || FileText;
    return (
      <button key={doc.id} onClick={() => setSelectedDoc(doc)}
        className={`w-full text-left rounded-lg border-l-4 ${borderColor} border bg-card px-4 py-3 flex items-center gap-3 hover:shadow-md transition-all`}
        data-testid={`doc-card-${doc.id}`}>
        <div className="p-1.5 rounded-lg bg-muted"><CatIcon className="w-3.5 h-3.5 text-muted-foreground" /></div>
        <div className="flex-1 min-w-0">
          <span className="text-xs font-semibold text-slate-700 truncate block">{doc.title || doc.filename}</span>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[10px] text-muted-foreground">{doc.patient_name}</span>
            <span className="text-[10px] text-muted-foreground">{doc.source_name}</span>
          </div>
        </div>
        <ArrowRight className="w-3.5 h-3.5 text-slate-300 shrink-0" />
      </button>
    );
  };

  return (
    <div className="space-y-4 mt-1" data-testid="my-documents-bucket">
      {/* NEEDS YOUR ATTENTION */}
      {needsAttention.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider mb-2 flex items-center gap-1">
            <Clock className="w-3 h-3" /> Needs Your Attention ({needsAttention.length})
          </p>
          <div className="space-y-1.5">
            {needsAttention.map(doc => renderCard(doc, 'border-l-amber-400'))}
          </div>
        </div>
      )}

      {/* WORKFLOW PENDING */}
      {workflowPending.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold text-blue-500 uppercase tracking-wider mb-2 flex items-center gap-1">
            <ArrowRight className="w-3 h-3" /> Workflow Pending ({workflowPending.length})
          </p>
          <div className="space-y-1.5">
            {workflowPending.map(doc => renderCard(doc, 'border-l-blue-400'))}
          </div>
        </div>
      )}
    </div>
  );
}


/* DocBucketCard removed — My Day cards now navigate to /documents for the full workflow */



function ExternalCorrespondenceView({ onComplete, autoOpenId, onAutoOpenConsumed }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openItem, setOpenItem] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [routeMsg, setRouteMsg] = useState('');
  const [activeAction, setActiveAction] = useState(null);
  const [seeding, setSeeding] = useState(false);
  // Nurse routing state
  const [nurses, setNurses] = useState([]);
  const [defaultNurse, setDefaultNurse] = useState(null);
  const [selectedNurse, setSelectedNurse] = useState(null);

  const fetchItems = useCallback(() => {
    api.get('/external-correspondence').then(r => setItems(r.data.items || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Load nurses + default preference
  useEffect(() => {
    api.get('/staff/nurses').then(r => setNurses(r.data.nurses || [])).catch(() => {});
    api.get('/providers/me/default-nurse').then(r => {
      if (r.data.default_nurse) { setDefaultNurse(r.data.default_nurse); setSelectedNurse(r.data.default_nurse); }
    }).catch(() => {});
  }, []);

  // 7K: Auto-open a specific correspondence item via deep link
  useEffect(() => {
    if (autoOpenId && !loading && items.length > 0) {
      (async () => {
        try {
          const res = await api.get(`/external-correspondence/${autoOpenId}`);
          setOpenItem(res.data.item);
          if (onAutoOpenConsumed) onAutoOpenConsumed();
        } catch {}
      })();
    }
  }, [autoOpenId, loading, items.length]); // eslint-disable-line

  const handleSeed = async () => {
    setSeeding(true);
    try { await api.post('/external-correspondence/seed-demo'); fetchItems(); }
    catch {} finally { setSeeding(false); }
  };

  const handleAcknowledge = async (itemId) => {
    try {
      await api.post(`/external-correspondence/${itemId}/acknowledge`, { physician_note: noteText });
      toast.success('Acknowledged'); setOpenItem(null); setNoteText(''); fetchItems(); onComplete();
    } catch { toast.error('Failed'); }
  };

  const handleRoute = async (itemId) => {
    if (!selectedNurse) { toast.error('Please select a nurse'); return; }
    try {
      const res = await api.post(`/external-correspondence/${itemId}/route`, {
        nurse_id: selectedNurse.id, nurse_name: selectedNurse.name, message: routeMsg,
      });
      toast.success(`Routed to ${res.data.routed_to_name}`);
      setOpenItem(null); setRouteMsg(''); fetchItems();
    } catch { toast.error('Failed'); }
  };

  const handleAiSummary = async (itemId) => {
    try {
      const res = await api.post(`/external-correspondence/${itemId}/ai-summary`);
      setOpenItem(prev => prev ? { ...prev, ai_summary: res.data.summary } : prev);
    } catch { toast.error('AI summary failed'); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="empty-ext-corr">
        <div className="p-4 rounded-2xl bg-muted/50 mb-4"><FileText className="w-8 h-8 text-muted-foreground" /></div>
        <p className="text-sm font-medium mb-1">No pending correspondence</p>
        <p className="text-xs text-muted-foreground mb-4">Outside records and documents appear here</p>
        <Button variant="outline" size="sm" onClick={handleSeed} disabled={seeding} data-testid="seed-corr-btn">
          {seeding ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}Load Demo Items
        </Button>
      </div>
    );
  }

  const STATUS_STYLES = {
    needs_review: 'border-l-amber-400',
    viewed: 'border-l-amber-400',
    routed: 'border-l-blue-400',
    acknowledged: 'border-l-emerald-400 opacity-60',
    completed: 'border-l-emerald-400 opacity-50',
  };

  const needsReview = items.filter(i => i.status === 'needs_review' || i.status === 'viewed');
  const workflowPending = items.filter(i => i.status === 'routed');

  return (
    <div className="space-y-4 mt-1" data-testid="ext-corr-view">
      {/* Section 1: Needs Review */}
      {needsReview.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider mb-2 flex items-center gap-1">
            <Clock className="w-3 h-3" /> Needs Review ({needsReview.length})
          </p>
          <div className="space-y-1.5">
            {needsReview.map(item => {
              const isOpen = openItem?.id === item.id;
              return (
                <CorrItemCard key={item.id} item={item} isOpen={isOpen}
                  onToggle={async () => {
                    if (isOpen) { setOpenItem(null); return; }
                    const res = await api.get(`/external-correspondence/${item.id}`);
                    setOpenItem(res.data.item); setActiveAction(null);
                  }}
                  openItem={openItem} activeAction={activeAction} setActiveAction={setActiveAction}
                  noteText={noteText} setNoteText={setNoteText} routeMsg={routeMsg} setRouteMsg={setRouteMsg}
                  handleAcknowledge={handleAcknowledge} handleRoute={handleRoute} handleAiSummary={handleAiSummary}
                  navigate={navigate} nurses={nurses} defaultNurse={defaultNurse} selectedNurse={selectedNurse}
                  setSelectedNurse={setSelectedNurse} bulkPharmPicker={bulkPharmPicker} setBulkPharmPicker={setBulkPharmPicker}
                  fetchItems={fetchItems}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Section 2: Workflow Pending */}
      {workflowPending.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold text-blue-500 uppercase tracking-wider mb-2 flex items-center gap-1">
            <ArrowRight className="w-3 h-3" /> Workflow Pending ({workflowPending.length})
          </p>
          <div className="space-y-1.5">
            {workflowPending.map(item => {
              const isOpen = openItem?.id === item.id;
              return (
                <CorrItemCard key={item.id} item={item} isOpen={isOpen}
                  onToggle={async () => {
                    if (isOpen) { setOpenItem(null); return; }
                    const res = await api.get(`/external-correspondence/${item.id}`);
                    setOpenItem(res.data.item); setActiveAction(null);
                  }}
                  openItem={openItem} activeAction={activeAction} setActiveAction={setActiveAction}
                  noteText={noteText} setNoteText={setNoteText} routeMsg={routeMsg} setRouteMsg={setRouteMsg}
                  handleAcknowledge={handleAcknowledge} handleRoute={handleRoute} handleAiSummary={handleAiSummary}
                  navigate={navigate} nurses={nurses} defaultNurse={defaultNurse} selectedNurse={selectedNurse}
                  setSelectedNurse={setSelectedNurse} bulkPharmPicker={bulkPharmPicker} setBulkPharmPicker={setBulkPharmPicker}
                  fetchItems={fetchItems}
                />
              );
            })}
          </div>
        </div>
      )}

      {items.length === 0 && (
        <div className="text-center py-12">
          <FileText className="w-8 h-8 mx-auto mb-2 text-slate-200" />
          <p className="text-sm text-slate-400">No pending correspondence</p>
        </div>
      )}
    </div>
  );
}

/* Extracted correspondence item card — used by both sections */

function CorrItemCard({ item, isOpen, onToggle, openItem, activeAction, setActiveAction,
  noteText, setNoteText, routeMsg, setRouteMsg, handleAcknowledge, handleRoute, handleAiSummary,
  navigate, nurses, defaultNurse, selectedNurse, setSelectedNurse, bulkPharmPicker, setBulkPharmPicker, fetchItems }) {

  const STATUS_STYLES = {
    needs_review: 'border-l-amber-400',
    viewed: 'border-l-amber-400',
    routed: 'border-l-blue-400',
    acknowledged: 'border-l-emerald-400 opacity-60',
    completed: 'border-l-emerald-400 opacity-50',
  };
  const style = STATUS_STYLES[item.status] || STATUS_STYLES.needs_review;

  return (
    <div className={`rounded-lg border-l-4 border bg-card ${style} ${isOpen ? 'shadow-md' : 'hover:shadow-sm'}`}
      data-testid={`corr-item-${item.id}`}>
      <button onClick={onToggle} className="w-full text-left px-4 py-3 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold truncate">{item.title}</span>
            <Badge variant="outline" className="text-[8px] h-3.5">{item.document_type?.replace(/_/g, ' ')}</Badge>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[10px] text-muted-foreground">{item.patient_name}</span>
            <span className="text-[10px] text-muted-foreground">{item.source_name}</span>
          </div>
          {item.routed_to_name && (
            <p className="text-[9px] text-blue-500 mt-0.5">Tasked to: {item.routed_to_name}</p>
          )}
        </div>
        <Badge variant="outline" className={`text-[9px] ${item.status === 'needs_review' ? 'bg-amber-50 text-amber-600' : item.status === 'routed' ? 'bg-blue-50 text-blue-600' : 'bg-emerald-50 text-emerald-600'}`}>
          {item.status === 'routed' ? `→ ${item.routed_to_name || 'Staff'}` : item.status?.replace(/_/g, ' ')}
        </Badge>
        <ArrowRight className={`w-3.5 h-3.5 text-muted-foreground/50 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
      </button>

            {/* Detail view — one-click deep */}
            {isOpen && openItem && (
              <div className="px-4 pb-4 border-t" data-testid={`corr-detail-${item.id}`}>
                {/* AI Summary */}
                <div className="py-3">
                  {openItem.ai_summary ? (
                    <div className="rounded-lg bg-blue-50/50 border border-blue-100/60 px-3 py-2.5 mb-3">
                      <p className="text-xs font-medium text-blue-600/80 mb-1 flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI Summary</p>
                      <p className="text-sm text-foreground leading-relaxed">{openItem.ai_summary}</p>
                    </div>
                  ) : (
                    <Button variant="outline" size="sm" className="text-xs gap-1 mb-3" onClick={() => handleAiSummary(item.id)}
                      data-testid={`corr-ai-btn-${item.id}`}>
                      <Sparkles className="w-3 h-3" /> Generate AI Summary
                    </Button>
                  )}

                  {/* Document content */}
                  <div className="rounded-lg bg-muted/40 px-3 py-2.5 mb-3">
                    <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{openItem.body}</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-1.5 mb-2">
                  {['acknowledge', 'route', 'conversation'].map(act => (
                    <button key={act} onClick={() => setActiveAction(activeAction === act ? null : act)}
                      className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${activeAction === act ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground hover:text-foreground'}`}
                      data-testid={`corr-action-${act}-${item.id}`}>
                      {act === 'acknowledge' ? 'Acknowledge' : act === 'route' ? 'Route to Nurse' : 'Start Conversation'}
                    </button>
                  ))}
                </div>

                {activeAction === 'acknowledge' && (
                  <div className="space-y-2 animate-in fade-in-0 duration-100">
                    <Textarea value={noteText} onChange={e => setNoteText(e.target.value)}
                      placeholder="Physician note..." className="min-h-[48px] text-sm resize-none" />
                    <Button size="sm" onClick={() => handleAcknowledge(item.id)} className="gap-1">
                      <Check className="w-3 h-3" /> Acknowledge & File
                    </Button>
                  </div>
                )}
                {activeAction === 'route' && (
                  <div className="space-y-2.5 animate-in fade-in-0 duration-100">
                    {/* Nurse picker */}
                    <div>
                      <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Route to</p>
                      <div className="space-y-1">
                        {nurses.map(nurse => (
                          <button key={nurse.id} onClick={() => setSelectedNurse(nurse)}
                            className={`w-full text-left px-3 py-2 rounded-xl border transition-all flex items-center gap-2.5 ${
                              selectedNurse?.id === nurse.id ? 'border-blue-400 bg-blue-50 shadow-sm' : 'border-slate-200 hover:border-slate-300'
                            }`}
                            data-testid={`nurse-pick-${nurse.id}`}>
                            <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-[10px] font-semibold text-blue-600">
                              {nurse.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className="text-xs font-medium text-slate-700">{nurse.name}</span>
                              {defaultNurse?.id === nurse.id && (
                                <Badge variant="outline" className="text-[8px] h-3.5 ml-1.5 bg-blue-50 text-blue-500 border-blue-200">Default</Badge>
                              )}
                            </div>
                            {selectedNurse?.id === nurse.id && <Check className="w-3.5 h-3.5 text-blue-500" />}
                          </button>
                        ))}
                        {nurses.length === 0 && <p className="text-[10px] text-slate-400 text-center py-2">No nurses available</p>}
                      </div>
                      {/* Set as default */}
                      {selectedNurse && selectedNurse.id !== defaultNurse?.id && (
                        <button onClick={async () => {
                          await api.put('/providers/me/default-nurse', { nurse_id: selectedNurse.id });
                          setDefaultNurse(selectedNurse);
                          toast.success(`${selectedNurse.name} set as default`);
                        }} className="text-[9px] text-blue-500 hover:text-blue-700 mt-1.5 block">
                          Set {selectedNurse.name} as my default nurse
                        </button>
                      )}
                    </div>
                    <Textarea value={routeMsg} onChange={e => setRouteMsg(e.target.value)}
                      placeholder={`Instructions for ${selectedNurse?.name || 'nurse'}...`} className="min-h-[48px] text-sm resize-none rounded-xl border-slate-200" />
                    <Button size="sm" onClick={() => handleRoute(item.id)} disabled={!selectedNurse} className="gap-1.5">
                      <Send className="w-3 h-3" /> Route to {selectedNurse?.name || 'Nurse'}
                    </Button>
                  </div>
                )}
                {activeAction === 'conversation' && (
                  <div className="space-y-2 animate-in fade-in-0 duration-100">
                    <Textarea value={noteText} onChange={e => setNoteText(e.target.value)}
                      placeholder="Start conversation about this document..." className="min-h-[48px] text-sm resize-none" />
                    <Button size="sm" onClick={async () => {
                      try {
                        await api.post(`/external-correspondence/${item.id}/start-conversation`, { title: '', body: noteText });
                        toast.success('Conversation started'); setOpenItem(null); setNoteText(''); fetchItems();
                      } catch { toast.error('Failed'); }
                    }} className="gap-1"><MessageSquare className="w-3 h-3" /> Start Conversation</Button>
                  </div>
                )}

                {/* 7K: Linked conversation — one-click navigation */}
                {openItem.linked_conversation_id && (
                  <div className="pt-2 mt-2 border-t" data-testid={`corr-linked-conv-${item.id}`}>
                    <button onClick={() => navigate(`/my-day?conv=${openItem.linked_conversation_id}`)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-[10px] font-medium bg-primary/5 hover:bg-primary/10 text-primary transition-colors"
                      data-testid={`corr-goto-conv-${item.id}`}>
                      <MessageSquare className="w-3 h-3" /> Open Linked Conversation <ArrowRight className="w-3 h-3" />
                    </button>
                  </div>
                )}

                {/* 7K: Patient chart quick link */}
                <div className="pt-2 mt-1 flex justify-end">
                  <Button variant="ghost" size="sm" className="text-[10px] gap-1"
                    onClick={() => navigate(`/chart/${openItem.patient_id}`)}
                    data-testid={`corr-open-chart-${item.id}`}>
                    Open Patient Chart <ArrowRight className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            )}
          </div>
    );
}

/* End of CorrItemCard */



/* ═══════════════════════════════════════
   ATTACHMENT BUTTON + PICKER (7P.5-7P.7)
   ═══════════════════════════════════════ */


function DocSignOffOverlay({ doc, onClose, viewingPdf, setViewingPdf }) {
  const [note, setNote] = useState('');
  const [taskInstructions, setTaskInstructions] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [recipients, setRecipients] = useState({ frequent: [], all_staff: [] });
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [showPicker, setShowPicker] = useState(false);

  // ── Per-provider view preference (summary vs pdf) ───────────────
  const getUserId = () => {
    try {
      const token = localStorage.getItem('prattos_token');
      if (!token) return 'default';
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.sub || payload.id || payload.email || 'default';
    } catch { return 'default'; }
  };
  const prefKey = `doc_view_mode_${getUserId()}`;
  const [viewMode, setViewMode] = useState(() => localStorage.getItem(prefKey) || 'summary');
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const loadPdf = async () => {
    if (pdfUrl) return; // already loaded
    setPdfLoading(true);
    try {
      const token = localStorage.getItem('prattos_token');
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/file`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (res.ok) {
        const ct = res.headers.get('content-type') || '';
        if (ct.includes('pdf') || ct.includes('image')) {
          const blob = await res.blob();
          setPdfUrl(URL.createObjectURL(blob));
        } else {
          setPdfUrl(null); // no physical file
        }
      }
    } catch {}
    finally { setPdfLoading(false); }
  };

  const toggleView = (mode) => {
    setViewMode(mode);
    localStorage.setItem(prefKey, mode);
    if (mode === 'pdf') loadPdf();
  };

  // Auto-load PDF if last preference was PDF
  useEffect(() => {
    if (viewMode === 'pdf') loadPdf();
  }, []); // eslint-disable-line

  useEffect(() => {
    api.get('/staff/my-frequent-recipients').then(r => {
      setRecipients(r.data);
      const def = (r.data.frequent || []).find(f => f.is_default);
      if (def) setSelectedRecipient(def);
      else if (r.data.frequent?.length > 0) setSelectedRecipient(r.data.frequent[0]);
    }).catch(() => {});
  }, []);

  const handleSignOff = async (sendToNurse) => {
    setSubmitting(true);
    try {
      await api.post(`/documents/${doc.id}/sign-off`, {
        physician_note: note || 'Noted',
        action_instructions: sendToNurse ? taskInstructions : '',
        send_to_nurse: sendToNurse,
        nurse_id: selectedRecipient?.id || '',
        nurse_name: selectedRecipient?.name || '',
      });
      toast.success(sendToNurse ? `Signed & sent to ${selectedRecipient?.name || 'nurse'}` : 'Signed & filed');
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  // PDF Viewer
  if (viewingPdf) {
    return (
      <div className="h-full flex flex-col rounded-xl border bg-white overflow-hidden">
        <div className="px-4 py-2.5 bg-slate-50 border-b flex items-center justify-between shrink-0">
          <span className="text-xs font-semibold text-slate-700 flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-500" /> {viewingPdf.title}
          </span>
          <button onClick={() => { URL.revokeObjectURL(viewingPdf.url); setViewingPdf(null); }}
            className="px-3 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-xs text-slate-600 font-medium">Close PDF</button>
        </div>
        <iframe src={viewingPdf.url} className="flex-1 w-full" title="Document" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid="doc-signoff-overlay">
      {/* Header */}
      <div className="flex items-center justify-between px-1 mb-3">
        <button onClick={onClose} className="text-xs text-blue-500 hover:text-blue-700 flex items-center gap-1">
          ← Back to documents
        </button>
        <span className="text-[10px] text-slate-400">{doc.patient_name}</span>
      </div>

      {/* Two-column layout */}
      <div className="flex-1 flex gap-4 min-h-0 overflow-hidden">
        {/* Left: Document content */}
        <div className="flex-1 rounded-xl border bg-white overflow-hidden flex flex-col">
          {/* Toggle bar */}
          <div className="flex items-center justify-between px-4 py-2.5 border-b bg-slate-50/70 shrink-0">
            <div className="flex items-center gap-1.5 p-0.5 rounded-lg bg-slate-200/60">
              <button onClick={() => toggleView('summary')}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-[11px] font-medium transition-all
                  ${viewMode === 'summary' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid="view-toggle-summary">
                <Sparkles className="w-3 h-3" /> Summary
              </button>
              <button onClick={() => toggleView('pdf')}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-[11px] font-medium transition-all
                  ${viewMode === 'pdf' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid="view-toggle-pdf">
                <FileText className="w-3 h-3" /> Original PDF
              </button>
            </div>
            <span className="text-[10px] text-slate-400">{doc.patient_name}</span>
            {/* Pop out — opens PDF in a separate window (great for dual monitors) */}
            {viewMode === 'pdf' && pdfUrl && (
              <button
                onClick={() => window.open(pdfUrl, '_blank', 'width=1000,height=900,resizable=yes,scrollbars=yes')}
                className="flex items-center gap-1 px-2 py-1 rounded-md border border-slate-200 hover:bg-slate-100 text-[10px] text-slate-500 hover:text-slate-700 transition-colors"
                title="Pop out to separate window (drag to second monitor)"
                data-testid="popout-pdf-btn">
                <ExternalLink className="w-3 h-3" /> Pop Out
              </button>
            )}
          </div>

          {/* Content area */}
          {viewMode === 'pdf' ? (
            <div className="flex-1 relative">
              {pdfLoading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              )}
              {!pdfLoading && pdfUrl && (
                <iframe src={pdfUrl} className="w-full h-full border-0" title="Original PDF" style={{ minHeight: '600px' }} />
              )}
              {!pdfLoading && !pdfUrl && (
                <div className="flex flex-col items-center justify-center h-full gap-3 py-16 text-slate-400">
                  <FileText className="w-10 h-10 opacity-30" />
                  <p className="text-sm">No PDF file attached to this document</p>
                  <p className="text-xs text-slate-300">Only OCR text is available — switch to Summary view</p>
                </div>
              )}
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto p-4">
              <h3 className="text-sm font-bold text-slate-700 mb-1">{doc.title || doc.filename}</h3>
              <p className="text-[10px] text-slate-400 mb-4">{doc.source_name} · {doc.patient_name}</p>

              {/* AI Clinical Brief */}
              {doc.ai_brief ? (
                <div className="space-y-3">
              {/* Document type + facility header */}
              <div className="rounded-xl bg-gradient-to-r from-blue-50 to-slate-50 border border-blue-100 px-4 py-3">
                <p className="text-xs font-bold text-blue-700">{doc.ai_brief.doc_type || 'Clinical Document'}</p>
                {doc.ai_brief.facility && <p className="text-[10px] text-blue-500">{doc.ai_brief.facility}</p>}
              </div>

              {/* Info grid */}
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 px-1">
                {doc.ai_brief.patient_name && <div className="flex gap-2 text-[11px]"><span className="text-slate-400 font-medium">Patient:</span><span className="text-slate-700">{doc.ai_brief.patient_name}</span></div>}
                {doc.ai_brief.date_of_service && <div className="flex gap-2 text-[11px]"><span className="text-slate-400 font-medium">DOS:</span><span className="text-slate-700">{doc.ai_brief.date_of_service}</span></div>}
                {doc.ai_brief.referring_physician && <div className="flex gap-2 text-[11px]"><span className="text-slate-400 font-medium">Referring:</span><span className="text-slate-700">{doc.ai_brief.referring_physician}</span></div>}
                {doc.ai_brief.specialist && <div className="flex gap-2 text-[11px]"><span className="text-slate-400 font-medium">Specialist:</span><span className="text-slate-700">{doc.ai_brief.specialist}</span></div>}
              </div>

              {/* Key sections */}
              {doc.ai_brief.reason_for_visit && (
                <div className="px-1"><p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1">Reason for Visit</p><p className="text-xs text-slate-700 leading-relaxed">{doc.ai_brief.reason_for_visit}</p></div>
              )}
              {doc.ai_brief.key_findings && (
                <div className="rounded-lg bg-amber-50/50 border border-amber-100 px-3 py-2.5"><p className="text-[9px] font-bold text-amber-600 uppercase tracking-wider mb-1">Key Findings</p><p className="text-xs text-slate-700 leading-relaxed">{doc.ai_brief.key_findings}</p></div>
              )}
              {doc.ai_brief.recommendations && (
                <div className="rounded-lg bg-emerald-50/50 border border-emerald-100 px-3 py-2.5"><p className="text-[9px] font-bold text-emerald-600 uppercase tracking-wider mb-1">Recommendations</p><p className="text-xs text-slate-700 leading-relaxed">{doc.ai_brief.recommendations}</p></div>
              )}

              {/* Bottom line */}
              {doc.ai_brief.one_line_summary && (
                <div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-2.5"><p className="text-[9px] font-bold text-blue-600 uppercase tracking-wider mb-1">Bottom Line</p><p className="text-sm font-medium text-slate-800">{doc.ai_brief.one_line_summary}</p></div>
              )}

              {/* Sign-off info if signed */}
              {doc.sign_off?.signed_at && (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2.5 flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                  <div><p className="text-[11px] font-semibold text-emerald-700">Signed by {doc.sign_off.signed_by_name}</p>
                    <p className="text-[10px] text-emerald-500">{new Date(doc.sign_off.signed_at).toLocaleString()}</p>
                    {doc.sign_off.physician_note && <p className="text-xs text-slate-700 mt-1">{doc.sign_off.physician_note}</p>}
                  </div>
                </div>
              )}

              {/* Collapsible full OCR text */}
              <details className="group">
                <summary className="text-[10px] text-slate-400 cursor-pointer hover:text-slate-600 select-none">Show full document text</summary>
                <div className="mt-2 rounded-lg bg-slate-50 p-3 max-h-64 overflow-y-auto">
                  <p className="text-[11px] text-slate-600 whitespace-pre-wrap font-mono leading-relaxed">{doc.ocr_text || 'No text available'}</p>
                </div>
              </details>
            </div>
          ) : (
            /* Fallback: raw OCR text if no AI brief */
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-[11px] text-slate-600 whitespace-pre-wrap font-mono leading-relaxed">{doc.ocr_text || 'No text available'}</p>
            </div>
          )}
          </div>
          )}
        </div>

        {/* Right: Actions */}
        <div className="w-72 shrink-0 rounded-xl border bg-white overflow-y-auto p-4 space-y-3">
          {/* View PDF button — only show when in summary mode */}
          {viewMode === 'summary' && (
          <Button size="sm" variant="outline" className="w-full h-9 text-[10px] gap-1.5 font-medium"
            onClick={() => toggleView('pdf')}>
            <Eye className="w-3 h-3" /> Switch to PDF View
          </Button>
          )}

          {/* Generate AI Brief — for docs that don't have one yet */}
          {!doc.ai_brief && (
            <Button size="sm" variant="outline" className="w-full h-9 text-[10px] gap-1.5 font-medium text-blue-600 border-blue-200"
              onClick={async () => {
                try {
                  toast.info('Generating AI brief...');
                  const res = await api.post('/doc-manager/generate-brief', { doc_id: doc.id });
                  if (res.data.ai_brief) {
                    toast.success('Brief generated');
                    onBack(); // Refresh the list to reload with brief
                  }
                } catch (e) { toast.error(e.response?.data?.detail || 'Failed to generate brief'); }
              }}
              data-testid={`generate-brief-btn-${doc.id}`}>
              <Sparkles className="w-3 h-3" /> Generate AI Brief
            </Button>
          )}

          {/* Physician Note */}
          <div>
            <label className="text-[9px] text-slate-500 uppercase font-medium">Physician Note</label>
            <textarea className="w-full mt-1 text-xs border rounded-lg px-2.5 py-2 min-h-[40px] resize-none"
              value={note} onChange={e => setNote(e.target.value)}
              placeholder="Optional — blank = 'Noted'" />
          </div>

          <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1 text-emerald-600 border-emerald-200"
            onClick={() => handleSignOff(false)} disabled={submitting}>
            {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
            Sign Off & File
          </Button>

          <div className="border-t border-slate-200 pt-2" />

          {/* Task Instructions */}
          <div>
            <label className="text-[9px] text-slate-500 uppercase font-medium">Task Instructions</label>
            <textarea className="w-full mt-1 text-xs border border-blue-200 rounded-lg px-2.5 py-2 min-h-[36px] resize-none"
              value={taskInstructions} onChange={e => setTaskInstructions(e.target.value)}
              placeholder="Schedule follow-up in 3 weeks..." />
          </div>

          {/* Send to nurse with picker */}
          <div className="flex gap-1.5">
            <Button size="sm" className="flex-1 h-8 text-[10px] gap-1 bg-blue-600 hover:bg-blue-700"
              onClick={() => handleSignOff(true)} disabled={submitting || !taskInstructions.trim()}>
              {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              Send to {selectedRecipient?.name || 'Nurse'}
            </Button>
            <div className="relative">
              <Button size="sm" variant="outline" className="h-8 px-2 border-blue-200"
                onClick={() => setShowPicker(!showPicker)}>
                <ChevronDown className="w-3 h-3 text-blue-500" />
              </Button>
              {showPicker && (
                <div className="absolute right-0 bottom-full mb-1 w-48 bg-white rounded-lg border shadow-lg z-20 py-1">
                  {recipients.frequent.map(r => (
                    <button key={r.id} onClick={() => { setSelectedRecipient(r); setShowPicker(false); }}
                      className={`w-full text-left px-2.5 py-1.5 text-[10px] hover:bg-blue-50 flex items-center gap-1.5 ${selectedRecipient?.id === r.id ? 'bg-blue-50' : ''}`}>
                      {r.is_default && <span className="text-amber-500">★</span>}
                      <span>{r.name}</span>
                      {selectedRecipient?.id === r.id && <Check className="w-2.5 h-2.5 text-blue-500 ml-auto" />}
                    </button>
                  ))}
                  {recipients.all_staff.filter(s => !recipients.frequent.find(f => f.id === s.id)).map(s => (
                    <button key={s.id} onClick={() => { setSelectedRecipient(s); setShowPicker(false); }}
                      className="w-full text-left px-2.5 py-1.5 text-[10px] hover:bg-blue-50">
                      <span>{s.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Defer + Reject — compact icons */}
          <div className="flex items-center gap-2 pt-1 border-t border-slate-100">
            <button onClick={async () => {
              const deferNote = note || 'Discuss at next visit';
              try {
                await api.post(`/documents/${doc.id}/sign-off`, {
                  physician_note: `[DEFERRED] ${deferNote}`, action_instructions: '', send_to_nurse: false,
                });
                const patId = doc.patient_id || doc.patient_match_candidates?.[0]?.patient_id;
                if (patId) {
                  await api.post(`/patients/${patId}/clinical-notes`, {
                    note_type: 'log', subject: `Chart Flag: ${doc.title || 'Document'}`,
                    body: `${deferNote}\n\nDeferred for discussion at next visit.`,
                  });
                }
                toast.success('Deferred — flag in chart'); onClose();
              } catch { toast.error('Failed'); }
            }} className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-medium text-slate-400 hover:text-amber-600">
              <Clock className="w-3 h-3" /> Defer
            </button>
            <button onClick={async () => {
              try {
                await api.post(`/documents/${doc.id}/review`, { action: 'reject', notes: note || 'Rejected — needs re-routing' });
                toast.success('Rejected — sent back to Doc Manager'); onClose();
              } catch { toast.error('Failed'); }
            }} className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-medium text-slate-400 hover:text-red-600">
              <X className="w-3 h-3" /> Reject
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════
   DELEGATED BUCKET — Items tasked to others
   ═══════════════════════════════════════ */



export { MyDocumentsBucketView, ExternalCorrespondenceView, CorrItemCard, DocSignOffOverlay };
